import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { KineticText } from "@/components/KineticText";
import Reveal from "@/components/Reveal";
import { BUSINESS, PRODUCTS } from "@/lib/content";
import { submitEnquiry } from "@/lib/api";

const EMPTY = {
  full_name: "",
  mobile: "",
  email: "",
  business_name: "",
  city: "",
  product: "",
  quantity: "",
  message: "",
};

const inputCls =
  "w-full bg-white border border-brand-forest/15 rounded-xl px-4 py-3 h-12 text-brand-forest placeholder:text-brand-muted/50 focus-visible:ring-2 focus-visible:ring-brand-leaf focus-visible:border-brand-leaf";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, product: params.get("product") || "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.mobile || !form.city || !form.product) {
      toast.error("Please fill in your name, mobile, city and product.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      setForm({ ...EMPTY });
      toast.success("Enquiry received!");
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact & Enquiry"
        description="Contact Pravin Hiralal Prajapati — wholesale vegetable supplier at Karnavati Market Yard, Vatva, Ahmedabad. Send a bulk enquiry, call, or chat on WhatsApp."
        path="contact"
      />

      <section className="relative pt-40 pb-14 md:pt-52 md:pb-20 max-w-screen-2xl mx-auto px-6 md:px-12">
        <span className="font-mono text-xs uppercase tracking-[0.28em] text-brand-terra">Get in touch</span>
        <KineticText
          as="h1"
          lines={["Let's talk", "fresh supply."]}
          className="font-display text-5xl md:text-8xl text-brand-forest mt-6 leading-[0.92] tracking-tight"
        />
      </section>

      <section className="pb-24 md:pb-32 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="space-y-5">
                <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="contact-phone" className="flex items-start gap-4 group">
                  <span className="h-11 w-11 rounded-full bg-brand-sand flex items-center justify-center text-brand-forest shrink-0"><Phone size={18} /></span>
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted block">Phone / WhatsApp</span>
                    <span className="text-brand-forest text-lg group-hover:text-brand-leaf transition-colors block">{BUSINESS.phone}</span>
                    <span className="text-brand-forest text-lg group-hover:text-brand-leaf transition-colors block">{BUSINESS.phone2}</span>
                  </span>
                </a>
                <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-start gap-4 group">
                  <span className="h-11 w-11 rounded-full bg-brand-sand flex items-center justify-center text-brand-forest shrink-0"><Mail size={18} /></span>
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted block">Email</span>
                    <span className="text-brand-forest text-lg group-hover:text-brand-leaf transition-colors break-all">{BUSINESS.email}</span>
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-full bg-brand-sand flex items-center justify-center text-brand-forest shrink-0"><MapPin size={18} /></span>
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted block">Location</span>
                    <span className="text-brand-forest text-lg">{BUSINESS.location}</span>
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <span className="h-11 w-11 rounded-full bg-brand-sand flex items-center justify-center text-brand-forest shrink-0"><Clock size={18} /></span>
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted block">Business Hours</span>
                    {BUSINESS.hours.map((h) => (
                      <span key={h.day} className="text-brand-forest block">{h.day}: <span className="text-brand-muted">{h.time}</span></span>
                    ))}
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-3xl border border-brand-forest/10 h-72">
                <iframe
                  title="Karnavati Agriculture Market Yard location"
                  src={BUSINESS.mapEmbed}
                  className="w-full h-full"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-white rounded-3xl border border-brand-forest/10 p-8 md:p-10 shadow-sm">
                {done ? (
                  <motion.div
                    data-testid="enquiry-success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-14"
                  >
                    <CheckCircle2 size={64} className="text-brand-leaf mx-auto" />
                    <h2 className="font-display text-3xl text-brand-forest mt-6">Thank you!</h2>
                    <p className="text-brand-muted mt-3 max-w-md mx-auto">
                      Your enquiry has been received. We will contact you shortly.
                    </p>
                    <button
                      data-testid="new-enquiry-btn"
                      onClick={() => setDone(false)}
                      className="mt-8 inline-flex items-center gap-2 rounded-full border border-brand-forest/20 text-brand-forest px-7 py-3 font-medium hover:bg-brand-forest hover:text-white transition-colors duration-300"
                    >
                      Send another enquiry
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} data-testid="enquiry-form" className="space-y-5">
                    <h2 className="font-display text-3xl text-brand-forest">Send an Enquiry</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="full_name" className="text-brand-forest">Full Name *</Label>
                        <Input id="full_name" data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Your name" />
                      </div>
                      <div>
                        <Label htmlFor="mobile" className="text-brand-forest">Mobile Number *</Label>
                        <Input id="mobile" data-testid="input-mobile" value={form.mobile} onChange={set("mobile")} className={inputCls} placeholder="+91 ..." />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-brand-forest">Email <span className="text-brand-muted/60">(optional)</span></Label>
                        <Input id="email" data-testid="input-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                      </div>
                      <div>
                        <Label htmlFor="business_name" className="text-brand-forest">Business Name <span className="text-brand-muted/60">(optional)</span></Label>
                        <Input id="business_name" data-testid="input-business" value={form.business_name} onChange={set("business_name")} className={inputCls} placeholder="Company / shop" />
                      </div>
                      <div>
                        <Label htmlFor="city" className="text-brand-forest">City *</Label>
                        <Input id="city" data-testid="input-city" value={form.city} onChange={set("city")} className={inputCls} placeholder="City" />
                      </div>
                      <div>
                        <Label className="text-brand-forest">Product Interested In *</Label>
                        <Select value={form.product} onValueChange={(v) => setForm((f) => ({ ...f, product: v }))}>
                          <SelectTrigger data-testid="select-product" className="h-12 rounded-xl border-brand-forest/15 bg-white text-brand-forest focus:ring-brand-leaf">
                            <SelectValue placeholder="Select a product" />
                          </SelectTrigger>
                          <SelectContent>
                            {PRODUCTS.map((p) => (
                              <SelectItem key={p.slug} value={p.name} data-testid={`product-option-${p.slug}`}>{p.name}</SelectItem>
                            ))}
                            <SelectItem value="Multiple / Other">Multiple / Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="quantity" className="text-brand-forest">Quantity</Label>
                      <Input id="quantity" data-testid="input-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 500 kg / 10 crates per day" />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-brand-forest">Message</Label>
                      <Textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} rows={4} className="w-full bg-white border border-brand-forest/15 rounded-xl px-4 py-3 text-brand-forest placeholder:text-brand-muted/50 focus-visible:ring-2 focus-visible:ring-brand-leaf focus-visible:border-brand-leaf" placeholder="Tell us about your requirement..." />
                    </div>
                    <button
                      type="submit"
                      data-testid="submit-enquiry-btn"
                      disabled={loading}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-forest text-brand-bone px-8 py-4 font-medium hover:bg-brand-forest-light transition-colors duration-300 active:scale-95 disabled:opacity-60"
                    >
                      {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Submit Enquiry <ArrowUpRight size={18} /></>}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
